import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Package, Truck, CheckCircle, ChevronDown, ChevronRight, ExternalLink, X } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use(cfg => { const t = localStorage.getItem('token'); if (t) cfg.headers.Authorization = `Bearer ${t}`; return cfg; });

const REASON_LABELS = {
  defective: 'Defective', damaged: 'Damaged', colour_fade: 'Colour Fade', shrinkage: 'Shrinkage',
  stitching_issue: 'Stitching Issue', neck_issue: 'Neck Issue', bubble_after_wash: 'Bubble After Wash',
  hole: 'Hole', tearing: 'Tearing',
};

const DefectGoods = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [expandedVendors, setExpandedVendors] = useState({});
  const [lightboxImg, setLightboxImg] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await api.get('/defect-goods');
      setData(res.data);
      // Auto-collapse all vendors by default
      setExpandedVendors({});
    } catch { toast.error('Failed to load defect goods'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleVendor = (vid) => setExpandedVendors(prev => ({ ...prev, [vid]: !prev[vid] }));

  // Group items by vendor
  const vendorGroups = {};
  (data.items || []).forEach(item => {
    const vid = item.vendor_id || 'unknown';
    if (!vendorGroups[vid]) vendorGroups[vid] = { vendor_name: item.vendor_name, items: [] };
    vendorGroups[vid].items.push(item);
  });

  const handleCreatePR = (vendorId, vendorName, items) => {
    // Navigate to Create PR with pre-filled data
    navigate('/create-purchase-return', {
      state: {
        prefillVendorId: vendorId,
        prefillItems: items.map(i => ({
          product_id: i.product_id, design_code: i.design_code,
          color: i.color, size: i.size, qty: i.defect_qty,
          rate: 0, reason: i.reason || 'defective',
        })),
      }
    });
  };

  const s = data.summary || {};

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="defect-goods-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Defect & Damage Goods</h1>
          <p className="text-sm text-gray-500 mt-1">Track defective returns and initiate vendor returns</p>
        </div>
        <button onClick={loadData} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" data-testid="defect-refresh-btn">
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="defect-kpi-total">
          <div className="flex items-center gap-2 text-red-600 mb-1">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Total Defect Pcs</span>
          </div>
          <p className="text-2xl font-bold text-red-700">{s.total_defect_pcs || 0}</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4" data-testid="defect-kpi-vendors">
          <div className="flex items-center gap-2 text-orange-600 mb-1">
            <Package className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Vendors Affected</span>
          </div>
          <p className="text-2xl font-bold text-orange-700">{s.vendors_affected || 0}</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4" data-testid="defect-kpi-pending">
          <div className="flex items-center gap-2 text-yellow-700 mb-1">
            <Truck className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Pending PR</span>
          </div>
          <p className="text-2xl font-bold text-yellow-800">{s.pending_pr || 0} pcs</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-testid="defect-kpi-created">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">PR Created</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{s.pr_created || 0} pcs</p>
        </div>
      </div>

      {/* Vendor Groups */}
      {Object.keys(vendorGroups).length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-gray-500 text-lg">No defective goods in stock</p>
          <p className="text-gray-400 text-sm mt-1">All clear!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(vendorGroups).map(([vid, group], vendorIdx) => {
            const isExpanded = expandedVendors[vid];
            const totalQty = group.items.reduce((s, i) => s + i.defect_qty, 0);
            const pendingItems = group.items.filter(i => i.status === 'pending');
            const pendingQty = pendingItems.reduce((s, i) => s + i.defect_qty, 0);

            return (
              <div key={vid} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`defect-vendor-group-${vid}`}>
                {/* Vendor Header */}
                <div
                  className="flex items-center justify-between px-5 py-3.5 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => toggleVendor(vid)}
                  data-testid={`defect-vendor-toggle-${vid}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-gray-500 w-6">{vendorIdx + 1}.</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">{group.vendor_name}</h3>
                      <p className="text-xs text-gray-500">{group.items.length} item(s) &middot; {totalQty} pcs defective</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                    {pendingQty > 0 && (
                      <span className="px-2.5 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                        {pendingQty} pcs pending
                      </span>
                    )}
                    {user?.role === 'admin' || user?.role === 'super_admin' && pendingItems.length > 0 && (
                      <button
                        className="px-4 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1.5"
                        onClick={e => { e.stopPropagation(); handleCreatePR(vid, group.vendor_name, pendingItems); }}
                        data-testid={`defect-create-pr-${vid}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Create PR
                      </button>
                    )}
                  </div>
                </div>

                {/* Items Table */}
                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#1E1E23] text-white text-xs uppercase tracking-wider">
                          <th className="px-3 py-2.5 text-center font-medium w-10">SR</th>
                          <th className="px-3 py-2.5 text-center font-medium w-16">Image</th>
                          <th className="px-4 py-2.5 text-left font-medium">Design</th>
                          <th className="px-3 py-2.5 text-left font-medium">Colour</th>
                          <th className="px-3 py-2.5 text-left font-medium">Size</th>
                          <th className="px-3 py-2.5 text-center font-medium">Defect Qty</th>
                          <th className="px-3 py-2.5 text-right font-medium">Disc%</th>
                          <th className="px-3 py-2.5 text-left font-medium">Reason</th>
                          <th className="px-3 py-2.5 text-left font-medium">Returned By</th>
                          <th className="px-3 py-2.5 text-center font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((item, idx) => (
                          <tr key={idx} className="border-t border-gray-100 hover:bg-red-50/30 transition-colors" data-testid={`defect-item-${vid}-${idx}`}>
                            <td className="px-3 py-2.5 text-center text-gray-500 font-mono text-xs">{idx + 1}</td>
                            <td className="px-3 py-2.5 text-center">
                              {item.image ? (
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${item.image}`}
                                  alt={item.design_code}
                                  className="w-10 h-10 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => { e.stopPropagation(); setLightboxImg(`${process.env.REACT_APP_BACKEND_URL}${item.image}`); }}
                                  data-testid={`defect-img-${vid}-${idx}`}
                                />
                              ) : (
                                <span className="text-gray-300 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 font-semibold text-gray-900">{item.design_code}</td>
                            <td className="px-3 py-2.5 text-gray-700">{item.color}</td>
                            <td className="px-3 py-2.5 text-gray-700">{item.size}</td>
                            <td className="px-3 py-2.5 text-center font-bold text-red-700">{item.defect_qty}</td>
                            <td className="px-3 py-2.5 text-right text-gray-700">{item.discount_percent > 0 ? `${item.discount_percent}%` : <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2.5">
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full capitalize">
                                {REASON_LABELS[item.reason] || item.reason?.replace(/_/g, ' ') || '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-gray-600 text-xs">
                              {(item.sr_sources || []).map((sr, si) => (
                                <div key={si}>{sr.customer} <span className="text-gray-400">({sr.credit_note || sr.return_number})</span></div>
                              ))}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              {item.status === 'pr_created' ? (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">PR Created</span>
                              ) : (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">Pending</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
          onClick={() => setLightboxImg(null)}
          data-testid="defect-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
            onClick={() => setLightboxImg(null)}
            data-testid="defect-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxImg}
            alt="Defect item"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default DefectGoods;
